/*
  The Audio Tailor: product page behaviour (loaded by main-product, product-videos and product-media-grid).
  No libraries. Uses the shared TAT helpers from theme.js.

  <tat-product>          variant picker, quantity limits, add to cart, pre-order confirmation,
                         server re-render of [data-dynamic] regions on variant change,
                         buy panel pinning, and the slim product bars (desktop top bar with links to every
                         [data-pdp-section] on the page plus scrollspy; phone bottom bar with price and CTA)
  <tat-product-gallery>  scroll-snap gallery, thumbnails, dots, lightbox <dialog> with tap-to-zoom; video/3D play on click
  <tat-product-recommendations> lazy-loads Shopify recommendations into the section
  <tat-lazy-video>       muted autoplay loop video that loads only near the viewport, with a mute toggle
*/

const TAT = window.TAT || {};
const desktop = window.matchMedia('(min-width: 64rem)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ */
/* <tat-product>                                                        */
/* ------------------------------------------------------------------ */
class TatProduct extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.mode = this.dataset.mode;
    this.form = this.querySelector('form[data-product-form]') || this.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.buyForm = this.querySelector('[data-buy-form]');
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.picker = this.querySelector('[data-picker]');
    this.select = this.querySelector('[data-variant-select]');
    this.qty = this.querySelector('[data-qty]');
    this.max = Number(this.buyForm?.dataset.max) || 0;
    this.requestId = 0;
    // Current variant, so the button state after an add matches the variant on screen.
    this.variant = this.select ? this.variants.find((v) => String(v.id) === this.select.value) : undefined;

    this.picker?.addEventListener('change', (e) => this.onOptionChange(e));
    this.select?.addEventListener('change', () => {
      const variant = this.variants.find((v) => String(v.id) === this.select.value);
      if (variant) this.setVariant(variant);
    });

    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.qty?.addEventListener('change', () => this.clampQty());
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-qty-minus]')) this.stepQty(-1);
      if (e.target.closest('[data-qty-plus]')) this.stepQty(1);
    });

    this.dialog = this.querySelector('[data-preorder-dialog]');
    this.dialog?.querySelector('[data-preorder-confirm]')?.addEventListener('click', () => {
      this.dialog.close();
      this.addToCart();
    });
    this.dialog?.querySelector('[data-preorder-cancel]')?.addEventListener('click', () => this.dialog.close());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });

    this.setupGiftCard();
    this.clampQty();
    this.updateOptionStates();
    this.setupPinning();
    this.setupBars();
  }

  disconnectedCallback() {
    this.cleanup?.forEach((fn) => fn());
    this.cleanup = [];
  }

  /* Buy panel pinning ------------------------------------------------ */

  // The panel is sticky on desktop. When it is taller than the window, pin it by its bottom edge instead
  // (a negative top), so every button stays reachable while the gallery scrolls.
  setupPinning() {
    this.cleanup = this.cleanup || [];
    const update = () => {
      const gap = 24;
      const top = Math.min(gap, window.innerHeight - this.offsetHeight - gap);
      this.style.setProperty('--pdp-sticky-top', `${top}px`);
    };
    update();
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(update);
      ro.observe(this);
      this.cleanup.push(() => ro.disconnect());
    }
    window.addEventListener('resize', update, { passive: true });
    this.cleanup.push(() => window.removeEventListener('resize', update));
  }

  /* Slim product bars ------------------------------------------------ */

  setupBars() {
    this.bars = [...this.querySelectorAll('[data-sticky-bar], [data-sticky-cta]')];
    if (!this.bars.length) return;

    this.addEventListener('click', (e) => {
      const submit = e.target.closest('[data-sticky-submit]');
      if (!submit || submit.disabled || submit.hasAttribute('data-modal-open')) return; // sold out: the contact pop-up opens instead
      const main = this.querySelector('[data-submit]');
      if (this.form.requestSubmit) this.form.requestSubmit(main || undefined);
      else main?.click();
    });

    // Show the bars once the buy form has scrolled up out of view (not before the customer reaches it).
    const target = this.buyForm || this.form;
    if ('IntersectionObserver' in window && target) {
      const io = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.bars.forEach((bar) => {
          bar.classList.toggle('is-visible', past);
          bar.inert = !past;
        });
      });
      io.observe(target);
      this.cleanup.push(() => io.disconnect());
    }

    this.buildNav();
  }

  // Links to every section that opted in with data-pdp-section="Label", in page order, with scrollspy.
  buildNav() {
    const nav = this.querySelector('[data-sticky-nav]');
    if (!nav) return;
    const sections = [...document.querySelectorAll('[data-pdp-section][id]')].filter((el) => el.dataset.pdpSection);
    if (sections.length < 2) {
      nav.hidden = true;
      return;
    }
    nav.innerHTML = '';
    const links = sections.map((el) => {
      const a = document.createElement('a');
      a.href = `#${el.id}`;
      a.textContent = el.dataset.pdpSection;
      a.className =
        'relative flex shrink-0 items-center whitespace-nowrap px-3 text-sm font-medium text-muted no-underline transition-colors hover:text-navy aria-[current=true]:text-navy after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:bg-transparent after:transition-colors aria-[current=true]:after:bg-accent xl:px-4 xl:after:inset-x-4';
      nav.append(a);
      return a;
    });

    if (!('IntersectionObserver' in window)) return;
    const visible = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => visible.set(entry.target, entry.isIntersecting));
        const current = sections.find((el) => visible.get(el));
        links.forEach((a, i) => {
          if (sections[i] === current) a.setAttribute('aria-current', 'true');
          else a.removeAttribute('aria-current');
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    sections.forEach((el) => io.observe(el));
    this.cleanup.push(() => io.disconnect());
  }

  /* Variant selection ------------------------------------------------ */

  selectedOptions() {
    return [...this.picker.querySelectorAll('fieldset')].map(
      (fs) => fs.querySelector('input:checked')?.value ?? null
    );
  }

  findVariant(options) {
    return this.variants.find((v) => v.options.every((o, i) => o === options[i]));
  }

  onOptionChange(e) {
    const fieldsets = [...this.picker.querySelectorAll('fieldset')];
    const changedIndex = Number(e.target.closest('fieldset')?.dataset.optionIndex || 0);
    const selected = this.selectedOptions();
    let variant = this.findVariant(selected);

    // No variant for this combination: keep the option just picked and use the closest variant.
    if (!variant) {
      const candidates = this.variants.filter((v) => v.options[changedIndex] === selected[changedIndex]);
      variant = candidates.find((v) => v.available) || candidates[0];
      if (!variant) return;
      variant.options.forEach((value, i) => {
        const input = [...fieldsets[i].querySelectorAll('input')].find((inp) => inp.value === value);
        if (input) input.checked = true;
      });
    }

    fieldsets.forEach((fs) => {
      const label = fs.querySelector('[data-option-selected]');
      const checked = fs.querySelector('input:checked');
      if (label && checked) label.textContent = checked.value;
    });

    this.setVariant(variant);
  }

  updateOptionStates() {
    if (!this.picker) return;
    const selected = this.selectedOptions();
    this.picker.querySelectorAll('fieldset').forEach((fs, i) => {
      fs.querySelectorAll('input').forEach((input) => {
        const options = [...selected];
        options[i] = input.value;
        const match = this.findVariant(options);
        input.toggleAttribute('data-unavailable', !match || !match.available);
      });
    });
  }

  setVariant(variant) {
    this.variant = variant;
    if (this.select) this.select.value = String(variant.id);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(window.history.state, '', url.toString());

    this.updateOptionStates();

    // Instant feedback while the server copy loads. The label comes from Liquid (data-button-label),
    // so purchase-mode labels such as Pre-order or Book your slot are never replaced.
    // Sold out variants become "Enquire about stock", opening the contact pop-up with the product filled in.
    const form = this.buyForm?.dataset || {};
    const name = form.singleVariant === 'true' ? form.productTitle : `${form.productTitle} (${variant.title})`;
    const setStock = (btn, submitType) => {
      if (this.mode === 'soldout') return; // a sold pre-loved item stays "Sold out", no stock enquiry
      btn.disabled = false;
      if (variant.available) {
        btn.type = submitType;
        btn.removeAttribute('data-modal-open');
        btn.textContent = form.buttonLabel;
      } else {
        btn.type = 'button';
        btn.dataset.modalOpen = 'contact';
        btn.dataset.topic = 'Price and availability';
        btn.dataset.message = `Hi, I'm interested in the ${name}. Could you let me know about stock and timing?`;
        btn.textContent = 'Enquire about stock';
      }
    };
    const submit = this.querySelector('[data-submit]');
    if (submit) setStock(submit, 'submit');
    this.querySelectorAll('[data-sticky-submit]').forEach((btn) => setStock(btn, 'button'));
    const payment = this.querySelector('[data-payment-button]');
    if (payment) payment.hidden = !variant.available;
    this.showError('');

    document.dispatchEvent(
      new CustomEvent('variant:change', { detail: { sectionId: this.sectionId, variant } })
    );
    this.renderVariant(variant.id);
  }

  async renderVariant(variantId) {
    const id = ++this.requestId;
    this.setAttribute('aria-busy', 'true');
    try {
      const html = await TAT.fetchSection(`${this.dataset.url}?variant=${variantId}`, this.sectionId);
      if (id !== this.requestId) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = [...doc.querySelectorAll('[data-dynamic]')];
      const current = [...this.querySelectorAll('[data-dynamic]')];
      const used = new Set();
      current.forEach((el) => {
        const match = fresh.find((f) => !used.has(f) && f.dataset.dynamic === el.dataset.dynamic);
        if (!match) return;
        used.add(match);
        el.innerHTML = match.innerHTML;
      });
    } catch (err) {
      // Keep the instant client-side state; the form still posts the right variant id.
      console.warn(err);
    } finally {
      if (id === this.requestId) this.removeAttribute('aria-busy');
    }
  }

  /* Quantity --------------------------------------------------------- */

  clampQty() {
    if (!this.qty) return 1;
    let value = parseInt(this.qty.value, 10);
    if (!Number.isFinite(value) || value < 1) value = 1;
    if (this.max > 0 && value > this.max) value = this.max;
    this.qty.value = value;
    const minus = this.querySelector('[data-qty-minus]');
    const plus = this.querySelector('[data-qty-plus]');
    if (minus) minus.disabled = value <= 1;
    if (plus) plus.disabled = this.max > 0 && value >= this.max;
    return value;
  }

  stepQty(delta) {
    if (!this.qty) return;
    this.qty.value = (parseInt(this.qty.value, 10) || 1) + delta;
    this.clampQty();
  }

  /* Gift card recipient ---------------------------------------------- */

  setupGiftCard() {
    const toggle = this.querySelector('[data-gift-toggle]');
    if (!toggle) return;
    const fields = this.querySelectorAll('[data-gift-fields] input, [data-gift-fields] textarea');
    const offset = this.querySelector('[data-gift-offset]');
    if (offset) offset.value = new Date().getTimezoneOffset().toString();
    const sync = () => fields.forEach((f) => (f.disabled = !toggle.checked));
    toggle.addEventListener('change', sync);
    sync();
  }

  /* Add to cart ------------------------------------------------------ */

  onSubmit(e) {
    e.preventDefault();
    if (this.mode === 'enquire' || this.mode === 'soldout') return;
    const submit = this.querySelector('[data-submit]');
    if (submit?.disabled) return;
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }
    if (this.mode === 'preorder' && this.dialog) {
      this.dialog.showModal();
      return;
    }
    this.addToCart();
  }

  async addToCart() {
    const submit = this.querySelector('[data-submit]');
    const data = new FormData(this.form);
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    const item = { id: Number(data.get('id')), quantity: this.clampQty() };
    if (Object.keys(properties).length) item.properties = properties;

    const label = submit?.textContent;
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      submit.textContent = 'Adding...';
    }
    this.showError('');

    try {
      await TAT.cart.add({ items: [item] });
      const drawer = document.getElementById('CartDrawer');
      if (TAT.settings?.cartType === 'drawer' && typeof drawer?.open === 'function') {
        drawer.open(submit);
      } else {
        window.location.href = TAT.routes?.cart || '/cart';
      }
    } catch (err) {
      // Cart API errors (422 etc.) carry a customer-friendly message; network errors ("Failed to fetch") don't.
      this.showError(
        err?.json?.description || (err?.status ? err.message : '') || 'Sorry, we could not add this to your cart. Please try again.'
      );
    } finally {
      if (submit) {
        submit.removeAttribute('aria-busy');
        submit.textContent = label;
        submit.disabled = this.variant ? !this.variant.available : false;
      }
    }
  }

  showError(message) {
    const el = this.querySelector('[data-form-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }
}
if (!customElements.get('tat-product')) customElements.define('tat-product', TatProduct);

/* ------------------------------------------------------------------ */
/* <tat-product-gallery>                                                */
/* ------------------------------------------------------------------ */
class TatProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.counter = this.querySelector('[data-counter]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.index = 0;

    this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
    this.next?.addEventListener('click', () => this.goTo(this.index + 1));
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumb))));
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.goTo(this.index - 1);
      if (e.key === 'ArrowRight') this.goTo(this.index + 1);
    });

    // Track which slide is in view (swipe, arrows, thumbnails all end here).
    if ('IntersectionObserver' in window) {
      this.slideObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.slideObserver.observe(slide));
    }

    this.track.addEventListener('click', (e) => {
      const zoom = e.target.closest('[data-zoom]');
      if (zoom) this.openLightbox(Number(zoom.dataset.zoom));
    });
    this.querySelector('[data-lightbox-open]')?.addEventListener('click', () => this.openLightbox(0));

    this.onVariantChange = (e) => {
      const { sectionId, variant } = e.detail || {};
      if (sectionId !== this.dataset.section || !variant?.media) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(variant.media));
      if (i < 0) return;
      if (desktop.matches) {
        // Desktop mosaic: bring the variant's image to the first (largest) tile.
        if (i > 0) {
          this.track.prepend(this.slides[i]);
          this.slides = [...this.track.querySelectorAll('[data-slide]')];
        }
        this.setActive(0);
        return;
      }
      this.goTo(i);
    };
    document.addEventListener('variant:change', this.onVariantChange);

    this.setupLightbox();
    this.updateArrows();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
    this.slideObserver?.disconnect();
    this.paneObserver?.disconnect();
  }

  goTo(i, smooth = true) {
    const index = Math.max(0, Math.min(this.slides.length - 1, i));
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto' });
    this.setActive(index);
  }

  setActive(index) {
    if (index < 0 || index === this.index) {
      this.updateArrows();
      return;
    }
    stopMedia(this.slides[this.index]);
    this.index = index;
    this.thumbs.forEach((t, i) => {
      if (i === index) {
        t.setAttribute('aria-current', 'true');
        this.revealThumb(t);
      } else {
        t.removeAttribute('aria-current');
      }
    });
    this.dots.forEach((d, i) => d.toggleAttribute('data-active', i === index));
    if (this.counter) this.counter.textContent = index + 1;
    this.updateArrows();
  }

  // Keep the active thumbnail visible in its row without scrolling the page.
  revealThumb(thumb) {
    const row = thumb.parentElement;
    if (!row || !row.offsetParent) return;
    const left = thumb.offsetLeft;
    const right = left + thumb.offsetWidth;
    if (left < row.scrollLeft) row.scrollTo({ left, behavior: 'smooth' });
    else if (right > row.scrollLeft + row.clientWidth) row.scrollTo({ left: right - row.clientWidth, behavior: 'smooth' });
  }

  updateArrows() {
    if (this.prev) this.prev.disabled = this.index <= 0;
    if (this.next) this.next.disabled = this.index >= this.slides.length - 1;
  }

  /* Lightbox --------------------------------------------------------- */

  setupLightbox() {
    this.dialog = document.getElementById(this.dataset.lightbox);
    if (!this.dialog) return;
    this.lbTrack = this.dialog.querySelector('[data-lightbox-track]');
    this.lbPanes = [...this.dialog.querySelectorAll('[data-zoom-pane]')];
    this.lbCounter = this.dialog.querySelector('[data-lightbox-counter]');
    this.lbIndex = 0;

    this.dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.lightboxGo(this.lbIndex - 1));
    this.dialog.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.lightboxGo(this.lbIndex + 1));
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.lightboxGo(this.lbIndex - 1);
      if (e.key === 'ArrowRight') this.lightboxGo(this.lbIndex + 1);
    });
    this.dialog.addEventListener('close', () => {
      stopMedia(this.dialog);
      this.lbPanes.forEach((p) => p.removeAttribute('data-zoomed'));
      document.documentElement.classList.remove('overflow-hidden');
    });

    // Tap an image to zoom to 200% (or natural size on desktop); tap again to zoom out.
    this.lbPanes.forEach((pane) => {
      pane.addEventListener('click', (e) => {
        if (!e.target.closest('img') || e.target.closest('[data-media-play], [data-external-video]')) return;
        const zoomed = pane.toggleAttribute('data-zoomed');
        if (zoomed) {
          const img = pane.querySelector('img');
          const rect = img.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width;
          const y = (e.clientY - rect.top) / rect.height;
          requestAnimationFrame(() => {
            pane.scrollLeft = x * pane.scrollWidth - pane.clientWidth / 2;
            pane.scrollTop = y * pane.scrollHeight - pane.clientHeight / 2;
          });
        }
      });
    });

    if ('IntersectionObserver' in window) {
      this.paneObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const next = this.lbPanes.indexOf(entry.target);
            if (next !== this.lbIndex) stopMedia(this.lbPanes[this.lbIndex]);
            this.lbIndex = next;
            if (this.lbCounter) this.lbCounter.textContent = this.lbIndex + 1;
          });
        },
        { root: this.lbTrack, threshold: 0.6 }
      );
      this.lbPanes.forEach((p) => this.paneObserver.observe(p));
    }
  }

  lightboxIndexSafe(i) {
    return Math.max(0, Math.min(this.lbPanes.length - 1, i));
  }

  lightboxGo(i) {
    const index = this.lightboxIndexSafe(i);
    const pane = this.lbPanes[index];
    if (!pane) return;
    this.lbPanes.forEach((p) => p.removeAttribute('data-zoomed'));
    if (index !== this.lbIndex) stopMedia(this.lbPanes[this.lbIndex]);
    this.lbTrack.scrollTo({ left: pane.offsetLeft - this.lbTrack.offsetLeft, behavior: 'auto' });
    this.lbIndex = index;
    if (this.lbCounter) this.lbCounter.textContent = index + 1;
  }

  openLightbox(i) {
    if (!this.dialog) return;
    this.dialog.showModal();
    document.documentElement.classList.add('overflow-hidden');
    requestAnimationFrame(() => this.lightboxGo(i));
  }
}
if (!customElements.get('tat-product-gallery')) customElements.define('tat-product-gallery', TatProductGallery);

/* ------------------------------------------------------------------ */
/* <tat-lazy-video>                                                     */
/* ------------------------------------------------------------------ */
class TatLazyVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-mute-toggle]');
    if (!this.video) return;

    this.toggle?.addEventListener('click', () => {
      this.video.muted = !this.video.muted;
      this.toggle.setAttribute('aria-pressed', String(!this.video.muted));
      this.toggle.setAttribute('aria-label', this.video.muted ? 'Turn sound on' : 'Turn sound off');
      if (!this.video.muted && this.video.paused) this.video.play().catch(() => {});
    });

    const load = () => {
      this.video.querySelectorAll('source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
      });
      this.video.load();
      if (reducedMotion.matches) {
        this.video.controls = true;
        return;
      }
      this.watch();
    };
    if (TAT.onVisible) TAT.onVisible(this, load, '300px');
    else load();
  }

  // Play only while on screen, to save battery and bandwidth.
  watch() {
    if (!('IntersectionObserver' in window)) {
      this.video.play().catch(() => {});
      return;
    }
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.video.play().catch(() => {});
          else this.video.pause();
        });
      },
      { threshold: 0.25 }
    ).observe(this);
  }
}
if (!customElements.get('tat-lazy-video')) customElements.define('tat-lazy-video', TatLazyVideo);

/* ------------------------------------------------------------------ */
/* <tat-product-recommendations>                                        */
/* ------------------------------------------------------------------ */
class TatProductRecommendations extends HTMLElement {
  connectedCallback() {
    const slot = this.querySelector('[data-recommendations-slot]');
    if (!slot?.dataset.url) return;
    const load = async () => {
      try {
        const res = await fetch(slot.dataset.url);
        if (!res.ok) throw new Error(`Recommendations failed: ${res.status}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const list = doc.querySelector('[data-recommendations-list]');
        if (list) slot.replaceWith(list);
        else this.remove();
      } catch (err) {
        this.remove();
      }
    };
    if (TAT.onVisible) TAT.onVisible(this, load, '600px');
    else load();
  }
}
if (!customElements.get('tat-product-recommendations')) {
  customElements.define('tat-product-recommendations', TatProductRecommendations);
}

/* ------------------------------------------------------------------ */
/* Page setup                                                           */
/* ------------------------------------------------------------------ */

// Accordions marked "open by default on desktop" are closed on smaller screens (they sit below the fold there).
// IsoAcoustics product selector: load the vendor script only when the block nears the viewport.
// Runs on load and again when the theme editor re-renders a section (module code only runs once).
function setupPage(scope = document) {
  if (!desktop.matches) {
    scope.querySelectorAll('details[data-open-desktop]').forEach((d) => d.removeAttribute('open'));
  }
  scope.querySelectorAll('[data-isoacoustics]').forEach((el) => {
    const src = el.dataset.isoacoustics;
    if (src && TAT.onVisible && TAT.loadScript) TAT.onVisible(el, () => TAT.loadScript(src).catch(() => {}));
  });
}
setupPage();
document.addEventListener('shopify:section:load', (e) => setupPage(e.target));

// Pause hosted videos and put YouTube/Vimeo posters back (a playing iframe can't be paused from here), so a video
// keeps no sound going once its gallery slide or lightbox pane is left or the lightbox closes.
function stopMedia(scope) {
  if (!scope) return;
  scope.querySelectorAll('video').forEach((v) => v.pause());
  scope.querySelectorAll('iframe').forEach((f) => {
    if (f.posterLink) f.replaceWith(f.posterLink);
  });
}

// Shopify video and 3D posters in the gallery: swap the poster for the player kept in the <template> on click.
document.addEventListener('click', (e) => {
  const button = e.target.closest('[data-media-play]');
  if (!button) return;
  const template = button.nextElementSibling;
  if (!(template instanceof HTMLTemplateElement)) return;
  e.preventDefault();
  const player = template.content.firstElementChild?.cloneNode(true);
  if (!player) return;
  button.replaceWith(player);
  template.remove();
  if (player.matches('video')) {
    player.play().catch(() => {});
    player.focus({ preventScroll: true });
  } else if (player.matches('model-viewer')) {
    window.Shopify?.loadFeatures?.([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (error) => {
          if (error || !window.Shopify?.ModelViewerUI) return;
          const ui = new window.Shopify.ModelViewerUI(player);
          ui.play?.();
        },
      },
    ]);
  }
});

// YouTube and Vimeo posters (gallery and videos section): swap the poster for the player on click.
document.addEventListener('click', (e) => {
  const link = e.target.closest('[data-external-video]');
  if (!link) return;
  e.preventDefault();
  const iframe = document.createElement('iframe');
  iframe.posterLink = link;
  iframe.src = link.dataset.externalVideo;
  iframe.title = link.getAttribute('aria-label') || 'Product video';
  iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
  iframe.allowFullscreen = true;
  iframe.className = 'absolute inset-0 size-full';
  link.replaceWith(iframe);
});
